import { useState } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Input from "aldoo-ra/Input"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import { SubmitSupportTicket } from "aldoo-ra/Admin/CustomerSupport/api"

const TEAM_SIZE_OPTIONS = ["1–10", "11–50", "51–200", "201–1000", "1000+"].map(
  (opt) => ({ value: opt, label: opt })
)

export default function ContactSalesDialog({ isOpen, onClose }) {
  const [form, setForm] = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")
  const texts = TextBundle({ id: "contact_sales_dialog" })
  const error_texts = TextBundle({ id: "error_texts" })

  function handleChange(e) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setSubmitting(true)
    setError("")
    try {
      const description = [
        form.message,
        form.company ? `Company: ${form.company}` : "",
        form.teamSize ? `Team size: ${form.teamSize}` : "",
      ].filter(Boolean).join("\n")

      const res = await SubmitSupportTicket({
        email: form.email,
        summary: `Sales inquiry from ${form.name || form.email}`,
        description,
        tags: ["sales"],
      })

      if (res?.result) {
        setSubmitted(true)
      } else {
        setError(res?.message || error_texts?.something_went_wrong || "Something went wrong. Please try again.")
      }
    } catch {
      setError(error_texts?.network_error || "Network error. Please check your connection and try again.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="snap_medium"
      zIndex={500}
      closeOnEscape={true}
      closeOnClickOutside={true}
      title={texts?.dialog_title}
      className="md:w-[680px]"
    >
        {/* Header */}
        <div className="md:border-b md:border-bordered pb-3 pt-1 mb-4 md:mb-8">
          <Typography 
            variant="txt_dim"
            className="block text-center md:text-left"
          >
            {texts?.dialog_subtitle}
          </Typography>
        </div>

        {/* Body */}
        <div>
          {submitted ? (
            <div className="text-center py-8">
              {/* Success icon */}
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-success/10 mx-auto mb-2">
                <svg className="w-8 h-8 text-success" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>

              <Typography
                component="h2"
                variant="h3"
              >
                {texts?.success_heading}
              </Typography>

              <Typography
                variant="body1"
                className="mb-8"
              >
                {texts?.success_message}
              </Typography>

              <Button
                onClick={onClose}
                variant="primary_m"
                className="min-w-[250px] mx-auto"
              >
                {texts?.close_btn_label}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                <div>
                  <Typography variant="label_accent">
                    {texts?.name_label}
                  </Typography>

                  <Input
                    type="text"
                    name="name"
                    variant="snap_compact"
                    value={form.name || ""}
                    onChange={handleChange}
                    placeholder={texts?.name_placeholder}
                    required
                  />
                </div>

                <div>
                  <Typography variant="label_accent">
                    {texts?.email_label}
                  </Typography>

                  <Input
                    type="email"
                    name="email"
                    variant="snap_compact"
                    value={form.email || ""}
                    onChange={handleChange}
                    placeholder={texts?.email_placeholder}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Typography variant="label_accent">
                    {texts?.company_label}
                  </Typography>

                  <Input
                    type="text"
                    name="company"
                    variant="snap_compact"
                    value={form.company || ""}
                    onChange={handleChange}
                    placeholder={texts?.company_placeholder}
                    required
                  />
                </div>

                <div>
                  <Typography variant="label_accent">
                    {texts?.teamsize_label}
                  </Typography>

                  <DropDownSelect
                    variant="snap_compact"
                    options={TEAM_SIZE_OPTIONS}
                    value={form.teamSize ? [{ value: form.teamSize, label: form.teamSize }] : []}
                    onChange={(selection) => setForm((prev) => ({ ...prev, teamSize: selection[0]?.value || "" }))}
                    placeholder={texts?.teamsize_option_text}
                    showCheckbox={false}
                    showSelectAll={false}
                    enableResetButton={false}
                  />
                </div>
              </div>

              <div>
                <Typography variant="label_accent">
                  {texts?.contact_reason}
                </Typography>

                <Input
                  multiline
                  autoFocus
                  name="message"
                  variant="snap_compact"
                  value={form.message || ""}
                  onChange={handleChange}
                  placeholder={texts?.reason_placeholder}
                  className="w-full"
                  styleOverrides={{
                    textarea: "h-20 bg-neutral  placeholder-text_secondary resize-none p-4 focus:outline-none focus:ring-0 scrollbar-thin scrollbar-thumb-neutral_2 scrollbar-track-transparent",
                    input_wrapper: "bg-neutral  rounded-2xl focus-within:border-primary transition-colors overflow-hidden",
                  }}
                />
              </div>

              {error && 
                <Typography 
                  variant="txt1" 
                  className="text-error px-1"
                > 
                  {error}
                </Typography>
              }

              <Button
                type="submit"
                disabled={submitting}
                variant="primary_m"
                className="min-w-[270px] mx-auto disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {texts?.cta_submitting_label}
                  </>
                ) : texts?.cta_submit_label}
              </Button>
            </form>
          )}
        </div>
    </Modal>
  )
}